import React from 'react'
import _ from 'lodash/fp'
import { connect } from 'react-redux'
import Fuse from 'fuse.js'
import { useMutation } from 'react-apollo'
import gql from 'graphql-tag'

import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'
import TextField from '@material-ui/core/TextField'
import CreatableSelect from 'react-select/creatable'

import { validator } from './Common'

import { makeStyles } from '@material-ui/core/styles'

import SplitContainer from '../../components/SplitContainer'

import apolloClient from '../../apollo'
import * as ClubsReducer from '../../reducers/clubs'
import { actions as authActions } from '../../reducers/auth'
import { actions as signupActions } from '../../reducers/signup'
import colors, { materialGreens } from '@fe/ui/colors'

import SnackBarRoot from '@fe/ui/SnackBarRoot'
import SnackWhen from '@fe/ui/SnackWhen'

const continueWithAuthSignupMutation = gql`
mutation continueWithAuthSignup($input: ContinueWithAuthSignupInput!) {
  continueWithAuthSignup(input: $input) {
    provider
    id
    otp
  }
}
`

const selectStyles = {
  menu: provided => ({
    ...provided,
    zIndex: 3
  })
}

const useStyles = makeStyles(theme => ({
  textField: {
    width: '100%',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  menu: {
    width: 200
  },
  avatar: {
    width: 120,
    height: 120,
    borderRadius: 60,
    margin: theme.spacing(1),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  buttonProgress: {
    color: materialGreens[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12
  },
  wrapper: {
    margin: theme.spacing(1),
    position: 'relative'
  }
}))

const validate = (profile, cameFromSocialSignup) => {
  const errors = {}
  if (!profile.name) {
    errors.name = 'Name is required'
  }

  if (!profile.email) {
    errors.email = 'Email address is required'
  } else if (!validator.isEmail(profile.email)) {
    errors.email = 'Email address is invalid'
  }

  if (!cameFromSocialSignup && !profile.password) {
    errors.password = 'Password is required'
  }

  return errors
}

const noOptionsMessage = ({ inputValue }) => {
  if (_.size(inputValue) < 2) {
    return 'Enter some letters to search'
  }
  return 'No clubs found'
}

const getClubName = (profile, clubs) => {
  const clubNameFromProfile = _.get('clubName', profile)
  if (clubNameFromProfile) {
    return clubNameFromProfile
  }
  return _.flow(
    _.get('clubId'),
    id => _.find({ id }, clubs),
    _.get('name')
  )(profile)
}

const Signup = props => {
  React.useEffect(() => {
    props.getClubs()
  }, [])

  const [signupError, setSignupError] = React.useState()

  const classes = useStyles()
  const [clubSearchQuery, setClubSearchQuery] = React.useState('')

  const state = _.get('location.state', props)
  const cameFromSocialSignup = _.get('cameFromSocialSignup', state)
  const socialSignupOutput = _.get('socialSignupOutput', state)
  const socialProfile = _.get('userProfile', state)

  const [touched, setTouched] = React.useState({})
  const [errors, setErrors] = React.useState({})
  const [profile, setProfile] = React.useState({ name: '', email: '', password: '', newsletter: true, ...socialProfile })

  const [socialSignup, socialSignupData] = useMutation(continueWithAuthSignupMutation, { client: apolloClient })

  React.useEffect(() => {
    setErrors(validate(profile, cameFromSocialSignup))
  }, [profile])
  const isValid = _.isEmpty(errors)

  const clubValue = _.get('clubId', profile)
    ? { name: getClubName(profile, props.clubs), value: _.get('clubId', profile) }
    : undefined
  const updateProfile = React.useCallback(field => event => {
    if (_.get('target', event)) {
      const value = event.target.value
      setProfile(_.set(field, value))
    } else {
      setProfile(_.set(field, event))
    }
  }, [setProfile])

  const fuse = React.useMemo(() => (
    new Fuse(props.clubs, {
      keys: _.getOr(['name'], 'keys', props),
      threshold: 0.15
    })
  ), [props.clubs])

  const clubs = React.useMemo(() => {
    if (_.size(clubSearchQuery) < 2) {
      return []
    }
    return _.take(20, fuse.search(clubSearchQuery))
  }, [fuse, clubSearchQuery])

  const getTextField = (key, options = { type: 'text', label: '' }) => {
    const error = _.get(key, errors)
    const showError = Boolean(_.get(key, touched) && error)
    const label = showError ? error : options.label
    return (
      <TextField
        margin='dense'
        fullWidth
        type={options.type}
        label={label}
        value={_.get(key, profile)}
        className={classes.textField}
        onChange={updateProfile(key)}
        variant='outlined'
        error={showError}
        onBlur={() => setTouched(_.set(key, true))}
      />
    )
  }

  const handleSignup = async () => {
    setSignupError()
    try {
      if (cameFromSocialSignup) {
        const { email, name, clubId, newsletter } = profile
        const clubName = clubId && !profile.clubName
          ? _.get('name', clubValue)
          : profile.clubName
        const { data: { continueWithAuthSignup } } = await socialSignup({
          variables: {
            input: {
              ..._.pick(['provider', 'id', 'otp'], socialSignupOutput),
              email,
              name,
              homeClub: clubId,
              homeClubName: clubName,
              newsletter
            }
          }
        })
        const { id, otp, provider } = continueWithAuthSignup
        props.doLogin({ email: id, password: otp, provider })
      } else {
        props.doSignup(profile)
      }
    } catch (err) {
      const errorMessage = _.flow(
        _.get('graphQLErrors'),
        _.map('message'),
        _.join(', ')
      )(err)
      setSignupError(errorMessage)
    }
  }

  return (
    <SplitContainer
      bottomBar={{
        leftText: 'Cancel',
        rightText: 'Create user',
        leftColor: '#888888',
        onLeftClick: () => props.history.push('/login'),
        disabledRight: !isValid,
        loading: _.get('loading', socialSignupData),
        onRightClick: handleSignup
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <h1 style={{ fontSize: '2em', fontWeight: 'bold' }}>
          Create your user
        </h1>
        To customize your GLFR experience, we need a little more details. Fill out the form below, and you are ready to get the best GLFR experience.

        {getTextField('name', { label: 'Your name' })}
        {getTextField('email', { type: 'email', label: 'Your email address' })}

        <FormControlLabel
          control={(
            <Checkbox
              style={{ color: colors.primaryDarkGreen }}
              checked={profile.newsletter}
              onChange={(_event, checked) => updateProfile('newsletter')(checked)}
            />
          )}
          label='Receive our newsletter with tips and tricks on how to get the most out of GLFR.'
        />

        {!cameFromSocialSignup && getTextField('password', { type: 'password', label: 'Choose a password' })}

        <CreatableSelect
          onChange={option => (
            setProfile(
              _.flow(
                _.set('clubId', _.get('id', option)),
                _.set('clubName', _.get('name', option))
              )
            )
          )}
          onInputChange={x => setClubSearchQuery(x)}
          styles={selectStyles}
          placeholder='Your home club'
          className={classes.textField}
          options={clubs}
          getOptionLabel={_.get('name')}
          getOptionValue={_.get('id')}
          value={clubValue}
          noOptionsMessage={noOptionsMessage}
          menuPlacement='auto'
          getNewOptionData={inputValue => ({
            name: inputValue,
            __isNew__: true
          })}
        />
      </div>
      <SnackWhen selector={() => signupError} type='error'>
        {signupError}
      </SnackWhen>
      <SnackBarRoot />
    </SplitContainer>
  )
}

const mapStateToProps = s => ({ clubs: _.flow(ClubsReducer.selectors.clubs, _.values, _.sortBy('name'))(s) })
const mapDispatchToProps = {
  getClubs: ClubsReducer.actions.clubsRequest,
  doLogin: authActions.loginRequest,
  doSignup: signupActions.signupRequest
}
export default connect(mapStateToProps, mapDispatchToProps)(Signup)
